import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Wallet,
  TrendingUp,
  CreditCard,
  Banknote,
  Building2,
  Smartphone,
  CheckCircle,
  PauseCircle,
  Edit,
  Trash2,
  ArchiveRestore,
  X,
  Save,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
} from 'lucide-react';
import styles from './RisorsePage.module.css';
import risorseService, { RisorsaRequest } from '../../services/risorseService';
import { Risorsa } from '../../types';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn, SummaryRow } from '@/utils/exports';
import { formatTipoRisorsa } from '@/constants/tipiRisorsa';

export const RisorsePage: React.FC = () => {
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResourceFormModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Risorsa | null>(null);
  const [editingRisorsa, setEditingRisorsa] = useState<Risorsa | null>(null);
  const [tipoRisorsa, setTipoRisorsa] = useState<'contanti' | 'conto_corrente' | 'carta' | 'altro'>('conto_corrente');
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    saldo_iniziale: '',
    attiva: true,
  });
  const [exportLoading, setExportLoading] = useState(false);

  const handleDeleteClick = (risorsa: Risorsa) => {
    setDeleteTarget(risorsa);
    setShowDeleteModal(true);
  };

  const handleEditRisorsa = (risorsa: Risorsa) => {
    setEditingRisorsa(risorsa);
    setTipoRisorsa(risorsa.tipo as typeof tipoRisorsa);
    setFormData({
      nome: risorsa.nome,
      descrizione: risorsa.descrizione || '',
      saldo_iniziale: String(risorsa.saldo_iniziale),
      attiva: risorsa.attiva,
    });
    setShowModal(true);
  };

  const handleNewRisorsa = () => {
    setEditingRisorsa(null);
    setTipoRisorsa('conto_corrente');
    setFormData({ nome: '', descrizione: '', saldo_iniziale: '', attiva: true });
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        await risorseService.deleteRisorsa(deleteTarget.id);
        await loadRisorse();
      } catch (err) {
        console.error('Error deleting risorsa:', err);
        setError('Errore nella cancellazione della risorsa');
      }
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  useEffect(() => {
    loadRisorse();
  }, []);

  const loadRisorse = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await risorseService.getRisorse();
      setRisorse(data);
    } catch (err) {
      console.error('Error loading risorse:', err);
      setError('Errore nel caricamento delle risorse');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (risorse.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'nome', header: 'Nome', type: 'text', width: 20 },
        { field: 'tipoDisplay', header: 'Tipo', type: 'text', width: 15 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 25 },
        { field: 'saldo_corrente', header: 'Saldo Effettivo', type: 'currency', width: 15 },
        { field: 'da_incassare', header: 'Da Incassare', type: 'currency', width: 15 },
        { field: 'da_pagare', header: 'Da Pagare', type: 'currency', width: 15 },
        { field: 'attivaDisplay', header: 'Attiva', type: 'text', width: 10 },
      ];

      const exportData = risorse.map(r => ({
        nome: r.nome,
        tipoDisplay: formatTipoRisorsa(r.tipo),
        descrizione: r.descrizione || '',
        saldo_corrente: r.saldo_corrente,
        da_incassare: r.da_incassare || 0,
        da_pagare: r.da_pagare || 0,
        attivaDisplay: r.attiva ? 'Sì' : 'No',
      }));

      const summaryData: SummaryRow[] = [
        { label: 'Totale Saldo', value: stats.saldoTotale, type: 'currency' },
        { label: 'Totale Da Incassare', value: stats.daIncassare, type: 'currency' },
        { label: 'Totale Da Pagare', value: stats.daPagare, type: 'currency' },
      ];

      const config = {
        title: 'Risorse',
        filename: 'risorse',
        columns,
        orientation: 'landscape' as const,
        summaryData,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting risorse:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSaveRisorsa = async () => {
    try {
      const request: RisorsaRequest = {
        nome: formData.nome,
        tipo: tipoRisorsa,
        descrizione: formData.descrizione || undefined,
        isActive: formData.attiva,
        saldoIniziale: parseFloat(formData.saldo_iniziale) || 0,
      };

      if (editingRisorsa) {
        await risorseService.updateRisorsa(editingRisorsa.id, request);
      } else {
        await risorseService.createRisorsa(request);
      }

      setShowModal(false);
      setFormData({ nome: '', descrizione: '', saldo_iniziale: '', attiva: true });
      setTipoRisorsa('conto_corrente');
      setEditingRisorsa(null);
      await loadRisorse();
    } catch (err) {
      console.error('Error saving risorsa:', err);
      setError('Errore nel salvataggio della risorsa');
    }
  };

  // Helper function to get icon based on tipo
  const getIconForTipo = (tipo: string) => {
    switch (tipo) {
      case 'conto_corrente':
        return Building2;
      case 'carta':
        return CreditCard;
      case 'contanti':
        return Banknote;
      case 'altro':
        return Smartphone;
      default:
        return Wallet;
    }
  };

  // Helper function to get color based on tipo
  const getColorForTipo = (tipo: string) => {
    switch (tipo) {
      case 'conto_corrente':
        return 'var(--primary-main)';
      case 'carta':
        return 'var(--info-main)';
      case 'contanti':
        return 'var(--success-main)';
      case 'altro':
        return '#0070BA';
      default:
        return 'var(--neutral-400)';
    }
  };

  const stats = {
    totale: risorse.length,
    saldoTotale: risorse.reduce((sum, r) => sum + (r.attiva ? r.saldo_corrente : 0), 0),
    daIncassare: risorse.reduce((sum, r) => sum + (r.attiva ? (r.da_incassare || 0) : 0), 0),
    daPagare: risorse.reduce((sum, r) => sum + (r.attiva ? (r.da_pagare || 0) : 0), 0),
    carteConti: risorse.filter((r) => ['conto_corrente', 'carta'].includes(r.tipo)).length,
    altriMetodi: risorse.filter((r) => !['conto_corrente', 'carta'].includes(r.tipo)).length,
  };

  return (
    <div className={styles.container}>
      <main>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Risorse</h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={risorse.length === 0}
              loading={exportLoading}
            />
            <button className={styles.btnPrimary} onClick={handleNewRisorsa}>
              <PlusCircle size={18} />
              <span className={styles.btnLabel}>Nuova</span>
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <Wallet size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{stats.totale}</span>
            <span className={styles.statText}>Risorse</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <TrendingUp size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>€{stats.saldoTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            <span className={styles.statText}>Saldo Effettivo</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <ArrowUpCircle size={20} style={{ color: 'var(--info-main)' }} />
            <span className={styles.statNumber}>€{stats.daIncassare.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            <span className={styles.statText}>Da Incassare</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <ArrowDownCircle size={20} style={{ color: 'var(--error-main)' }} />
            <span className={styles.statNumber}>€{stats.daPagare.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            <span className={styles.statText}>Da Pagare</span>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Caricamento risorse...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--error-main)' }}>
            <p>{error}</p>
          </div>
        )}

        {/* Risorse Grid - Compact Cards */}
        {!loading && !error && (
          <div className={styles.resourcesGrid}>
            {risorse.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                <p>Nessuna risorsa trovata. Crea la tua prima risorsa!</p>
              </div>
            ) : (
              risorse.map((risorsa) => {
                const IconComponent = getIconForTipo(risorsa.tipo);
                const color = getColorForTipo(risorsa.tipo);
                return (
                  <div
                    key={risorsa.id}
                    className={`${styles.resourceCard} ${!risorsa.attiva ? styles.inactive : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardIcon} style={{ background: risorsa.attiva ? color : 'var(--neutral-300)' }}>
                        <IconComponent size={20} style={{ color: 'white' }} />
                      </div>
                      <div className={styles.cardInfo}>
                        <h4 className={styles.cardName}>{risorsa.nome}</h4>
                        <span className={styles.cardBank}>{risorsa.descrizione || 'Nessuna descrizione'}</span>
                      </div>
                      {risorsa.attiva ? (
                        <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                          <CheckCircle size={10} />
                        </span>
                      ) : (
                        <span className={styles.badgeInactive}>
                          <PauseCircle size={10} />
                        </span>
                      )}
                    </div>
                    <div className={styles.cardBalance}>
                      <span className={styles.balanceLabel}>Saldo Effettivo</span>
                      <span className={`${styles.balanceValue} ${risorsa.attiva ? styles.positive : ''}`}>
                        € {risorsa.saldo_corrente.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className={styles.cardStats}>
                      <div className={styles.cardStat}>
                        <ArrowUpCircle size={12} style={{ color: 'var(--info-main)' }} />
                        <span className={styles.statLabel}>Da Incassare</span>
                        <span className={styles.statValue} style={{ color: 'var(--info-main)' }}>
                          € {(risorsa.da_incassare || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className={styles.cardStat}>
                        <ArrowDownCircle size={12} style={{ color: 'var(--error-main)' }} />
                        <span className={styles.statLabel}>Da Pagare</span>
                        <span className={styles.statValue} style={{ color: 'var(--error-main)' }}>
                          € {(risorsa.da_pagare || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    <div className={styles.cardActions}>
                      <button className={styles.btnSmall} onClick={() => handleEditRisorsa(risorsa)}>
                        {risorsa.attiva ? <><Edit size={14} /> Modifica</> : <><ArchiveRestore size={14} /> Ripristina</>}
                      </button>
                      <button className={styles.btnIcon} onClick={() => handleDeleteClick(risorsa)}><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Modal */}
      {showResourceFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editingRisorsa ? 'Modifica Risorsa' : 'Nuova Risorsa'}</h3>
              <button className={styles.modalClose} onClick={() => { setShowModal(false); setEditingRisorsa(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.resourceOptions}>
                {[
                  { value: 'conto_corrente' as const, icon: Building2, label: 'Conto' },
                  { value: 'carta' as const, icon: CreditCard, label: 'Carta' },
                  { value: 'altro' as const, icon: Smartphone, label: 'Altro' },
                  { value: 'contanti' as const, icon: Banknote, label: 'Contanti' },
                ].map((opt) => (
                  <label key={opt.value} className={`${styles.resourceOption} ${tipoRisorsa === opt.value ? styles.selected : ''}`}>
                    <input type="radio" name="tipo" value={opt.value} checked={tipoRisorsa === opt.value} onChange={(e) => setTipoRisorsa(e.target.value as typeof tipoRisorsa)} />
                    <opt.icon size={20} />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder="Es: Conto principale"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Descrizione</label>
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder="Es: Intesa Sanpaolo - •••• 1234"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Saldo Iniziale</label>
                <input
                  type="number"
                  className={styles.formControl}
                  placeholder="0.00"
                  step="0.01"
                  value={formData.saldo_iniziale}
                  onChange={(e) => setFormData({ ...formData, saldo_iniziale: e.target.value })}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => { setShowModal(false); setEditingRisorsa(null); }}>Annulla</button>
              <button
                className={styles.btnPrimary}
                onClick={handleSaveRisorsa}
                disabled={!formData.nome.trim()}
              >
                <Save size={16} /> Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={40} />
            </div>
            <h3 className={styles.confirmTitle}>Eliminare questa risorsa?</h3>
            <p className={styles.confirmText}>
              Stai per eliminare <strong>{deleteTarget.nome}</strong>.
              Questa azione non può essere annullata.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmDelete}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
